import React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Anchor, Badge, Button, Group, SimpleGrid, Skeleton, Stack, Text } from "@mantine/core";
import { Alert, Card, PageHeader, SectionCard, StatusBadge, spacePx } from "@xplatform/design-system";
import { IconArrowLeft, IconReceipt } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { TransactionDetail } from "@models/adminMonitoring";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import { CapabilityTable, EventsTable, SourceAlerts, UsageSplitTable } from "@features/Insights";
import type { UsageSplit } from "@models/adminMonitoring";

/** A split worth showing: it has a value other than "unknown". */
const telling = (rows: UsageSplit[] | undefined): rows is UsageSplit[] =>
  Boolean(rows?.some((r) => r.value !== "unknown"));
import { describeReference, formatCount, formatDateTime, formatMoney, moduleLabel } from "@utils/monitoringFormat";

const ENTITY_ROUTES: Record<string, (id: string) => string> = {
  extraction_process: (id) => `/admin/monitoring/process/${encodeURIComponent(id)}`,
  mapping_job: (id) => `/admin/monitoring/mapping-job/${encodeURIComponent(id)}`,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack gap={spacePx[1]}>
    <Text size="xs" c="dimmed">{label}</Text>
    <Text size="sm" component="div">{children}</Text>
  </Stack>
);

/**
 * One transaction: its reference, totals, cost per capability and the events
 * behind it. Loads entirely from the URL (group id + optional window), so a
 * reload or a shared link shows the same thing.
 */
export const TransactionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId = "" } = useParams();
  const [params] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const window = from && to ? { from, to } : undefined;

  const detail = useApiResource<TransactionDetail>(`tx:${groupId}:${from}:${to}`, (signal) =>
    adminMonitoringApi().transaction(groupId, window, signal));
  const data = detail.data;
  const tx = data?.transaction;
  const entityRoute = tx?.entity?.id ? ENTITY_ROUTES[tx.entity.kind] : undefined;

  return (
    <Stack gap="md">
        <Button variant="subtle" w="fit-content" leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(-1)}>
          {t("admin.insights.detail.back")}
        </Button>
        <PageHeader
          title={tx ? describeReference(tx) === "-" ? t("admin.insights.uncorrelated") : describeReference(tx)
                    : t("admin.insights.detail.title")}
          description={tx ? `${moduleLabel(tx.module_name)} · ${tx.service_name ?? "-"}` : undefined}
          icon={<IconReceipt size={28} />}
        />

        {detail.error && <Alert color={detail.forbidden ? "gray" : "red"}>{detail.error}</Alert>}
        <SourceAlerts sources={data?.sources} />

        {detail.loading && !data ? (
          <Skeleton h={240} />
        ) : tx && data ? (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              <Card>
                <Field label={t("admin.insights.columns.cost")}>
                  <Text fw={700} fz="h3">{formatMoney(tx.total_cost, tx.currency)}</Text>
                  {tx.unpriced_count > 0 && (
                    <Text size="xs" c="orange.8">{t("admin.insights.unpricedBadge", { count: tx.unpriced_count })}</Text>
                  )}
                </Field>
              </Card>
              <Card><Field label={t("admin.insights.columns.events")}><Text fw={700} fz="h3">{formatCount(tx.event_count)}</Text></Field></Card>
              <Card><Field label={t("admin.insights.detail.firstUsed")}>{formatDateTime(tx.first_used)}</Field></Card>
              <Card><Field label={t("admin.insights.detail.lastUsed")}>{formatDateTime(tx.last_used)}</Field></Card>
            </SimpleGrid>

            <SectionCard title={t("admin.insights.detail.reference")}>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                <Field label={t("admin.insights.detail.kind")}>
                  <Badge variant="light">{t(`admin.insights.detail.kinds.${tx.kind}`)}</Badge>
                </Field>
                <Field label={t("admin.insights.detail.refType")}><Text ff="monospace" size="sm">{tx.external_ref_type ?? "-"}</Text></Field>
                <Field label={t("admin.insights.detail.refId")}><Text ff="monospace" size="sm">{tx.external_ref_id ?? "-"}</Text></Field>
                <Field label={t("admin.insights.columns.organisation")}>{tx.organisation_name ?? tx.organisation_id ?? "-"}</Field>
                <Field label={t("admin.insights.columns.user")}>
                  {tx.users_count > 1 ? t("admin.insights.usersCount", { count: tx.users_count }) : tx.user_name ?? tx.user_id ?? "-"}
                </Field>
                <Field label={t("admin.insights.detail.models")}>{[...tx.providers, ...tx.models].join(", ") || "-"}</Field>
                <Field label={t("admin.insights.detail.window")}>
                  {data.window ? `${formatDateTime(data.window.start)} → ${formatDateTime(data.window.end)}` : t("admin.insights.detail.lifetime")}
                </Field>
                {window && (
                  <Field label={t("admin.insights.detail.scope")}>
                    <Anchor component={Link} to={`/admin/monitoring/transactions/${groupId}`}>{t("admin.insights.detail.showLifetime")}</Anchor>
                  </Field>
                )}
              </SimpleGrid>
            </SectionCard>

            {tx.entity && (
              <SectionCard title={t(`admin.insights.entities.${tx.entity.kind}`, { defaultValue: tx.entity.kind })}
                           actions={entityRoute && tx.entity.id ? (
                             <Button size="xs" variant="light" onClick={() => navigate(entityRoute(tx.entity!.id!), {
                               state: {
                                 createdBy: tx.entity!.created_by_name ?? undefined,
                                 organisationName: tx.organisation_name ?? undefined,
                                 doctypeName: tx.entity!.label ?? undefined,
                               },
                             })}>
                               {t("admin.insights.detail.openEntity")}
                             </Button>
                           ) : undefined}>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                  <Field label={t("admin.insights.detail.entityId")}><Text ff="monospace" size="sm">{tx.entity.id ?? "-"}</Text></Field>
                  <Field label={t("admin.insights.detail.status")}>
                    {tx.entity.status ? <StatusBadge status={tx.entity.status} size="sm" /> : (
                      <Text size="sm" c="dimmed">{t("admin.insights.detail.noMetadata")}</Text>
                    )}
                  </Field>
                  {tx.entity.label && <Field label={t("admin.insights.detail.label")}>{tx.entity.label}</Field>}
                  {tx.entity.created_by_name && <Field label={t("admin.insights.detail.createdBy")}>{tx.entity.created_by_name}</Field>}
                  {Object.entries(tx.entity.details).map(([name, value]) => (
                    <Field key={name} label={name}>{String(value)}</Field>
                  ))}
                </SimpleGrid>
              </SectionCard>
            )}

            <SectionCard title={t("admin.insights.detail.byCapability")}>
              <CapabilityTable rows={data.capabilities} />
            </SectionCard>

            {telling(data.resources) && data.resources.length > 1 && (
              <SectionCard title={t("admin.insights.detail.byResource")}>
                <UsageSplitTable rows={data.resources} header={t("admin.insights.detail.resource")} />
              </SectionCard>
            )}
            {telling(data.steps) && (
              <SectionCard title={t("admin.insights.detail.byStep")}>
                <UsageSplitTable rows={data.steps} header={t("admin.insights.detail.step")} />
              </SectionCard>
            )}
            {telling(data.items) && (
              <SectionCard title={t("admin.insights.detail.byItem")}>
                <UsageSplitTable rows={data.items} header={t("admin.insights.detail.item")} />
              </SectionCard>
            )}

            <SectionCard title={t("admin.insights.detail.events")}>
              <Stack gap="xs">
                {!data.events_complete && (
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">{t("admin.insights.detail.eventsTruncated", { shown: data.events.length, total: tx.event_count })}</Text>
                  </Group>
                )}
                <EventsTable items={data.events} loading={false} showOrganisation={false} maxHeight={560} />
              </Stack>
            </SectionCard>
          </>
        ) : null}
    </Stack>
  );
};

export default TransactionDetailPage;
